import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * 1NOW-11153: where the search bar in the top bar is allowed to show.
 *
 * Never on checkout or the car pages, where the renter is already inside one
 * booking. The booking-details and payment-success pages come after that and
 * never carry a search either.
 */
const HIDDEN_PATHS = [
  "/checkout",
  "/listing-car-detail",
  "/car-listing-detail",
  "/booking-details",
  "/payment-success",
];
const HOME_PATHS = ["/", "/home"];

export const topBarSearchAllowedOn = (pathname: string) =>
  !HIDDEN_PATHS.includes(pathname);

/** Extra scroll the home page needs before the bar hides again, so the header
 *  growing by the bar's own height cannot flip the answer back and forth. */
const HYSTERESIS_PX = 96;

/**
 * Has the first visible element matching `selector` scrolled up under the
 * header? False while there is no such element (or off the home page).
 * Desktop and mobile copies of a section are both in the tree; one is
 * display:none, so the visible one is used.
 */
export const usePastAnchor = (selector: string, enabled = true): boolean => {
  const [past, setPast] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const check = () => {
      const anchor = Array.from(document.querySelectorAll<HTMLElement>(selector)).find(
        (el) => el.offsetParent !== null,
      );
      const headerH =
        document.querySelector<HTMLElement>(".nc-Header")?.offsetHeight ?? 0;
      if (!anchor) {
        setPast(false);
        return;
      }
      const bottom = anchor.getBoundingClientRect().bottom;
      setPast((was) =>
        was ? bottom <= headerH + HYSTERESIS_PX : bottom <= headerH,
      );
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check, { passive: true });
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [selector, enabled]);

  return enabled && past;
};

/**
 * Should the header carry the search bar right now?
 *
 * Inner pages: always, unless the path is one of the hidden ones. Home: only
 * once the page's own big search form has scrolled up under the header, so
 * the two are never on screen together (as on the DFW site). Design A's form
 * sits in its banner (SectionHero3); Design B's sits below the banner and is
 * marked data-topbar-search.
 */
export const useTopBarSearchVisible = (): boolean => {
  const { pathname } = useLocation();
  const isHome = HOME_PATHS.includes(pathname);
  const pastForm = usePastAnchor('[data-nc-id="SectionHero3"], [data-topbar-search]', isHome);

  if (!topBarSearchAllowedOn(pathname)) return false;
  return isHome ? pastForm : true;
};

/**
 * Should the pinned copy of the top bar (PinnedHeader) be up right now?
 *
 * Only where the search may show. Home: once the banner has gone, Design B's
 * (its header and hero band) or Design A's (the hero with its search form).
 * Other pages: as soon as the page's own header starts to scroll away, so the
 * copy takes its place with no gap, as `sticky` would if #root let it.
 */
export const usePinnedHeaderVisible = (): boolean => {
  const { pathname } = useLocation();
  const allowed = topBarSearchAllowedOn(pathname);
  const isHome = HOME_PATHS.includes(pathname);
  const pastBanner = usePastAnchor(
    '[data-topbar-banner], [data-nc-id="SectionHero3"]',
    allowed && isHome,
  );
  const [pastHeader, setPastHeader] = useState(false);

  useEffect(() => {
    if (!allowed || isHome) return;
    const check = () => {
      // Design B's About and Contact draw a header in each of their desktop
      // and phone banners; the one on screen counts.
      const header = Array.from(document.querySelectorAll<HTMLElement>(".nc-Header")).find(
        (el) => el.offsetParent !== null,
      );
      setPastHeader(!!header && header.getBoundingClientRect().top < 0);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check, { passive: true });
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [pathname, allowed, isHome]);

  if (!allowed) return false;
  return isHome ? pastBanner : pastHeader;
};
